import type { EngineWorkscopeEvent } from '@avisoma/shared';
import { SourcePageCell } from './SourcePageCell';
import { Td, Th, TruncatedTd } from './cells';
import { FindingMarkerCell, FindingMarkerHeader, type RowFindings } from './FindingMarkerCell';
import {
  formatModulesWorked,
  formatWorkOrders,
  formatWorkscopeClock,
  formatWorkscopeLevel,
} from './workscopePresentation';
import {
  extractionRowClass,
  extractionTableClass,
  extractionTbodyClass,
  extractionTheadClass,
} from './tableClasses';

/**
 * Dense grid for consolidated shop-visit workscope events (DR-3126).
 *
 * Unlike the other extraction tables a row carries `pageIds` (plural): consolidation
 * groups every page of one physical shop visit into a single event, so the source
 * column lists each contributing page. Pass no `pageIdToPosition` to hide that column
 * on views where every row belongs to the same page.
 *
 * The `workscopeLevelRaw` / `dateRaw` / `dateLabel` evidence lanes stay out of the
 * grid and are read from the row inspector.
 */
export function WorkscopeTable({
  rows,
  pageIdToPosition,
  onInspect,
  rowFindings,
}: {
  rows: EngineWorkscopeEvent[];
  pageIdToPosition?: Map<string, number>;
  onInspect: (row: EngineWorkscopeEvent, index: number) => void;
  rowFindings?: RowFindings;
}) {
  return (
    <table className={extractionTableClass}>
      <thead className={extractionTheadClass}>
        <tr>
          {rowFindings && <FindingMarkerHeader />}
          {pageIdToPosition && <Th>Pages</Th>}
          <Th>Shop Visit</Th>
          <Th>Work Orders</Th>
          <Th>Level</Th>
          <Th>Modules Worked</Th>
          <Th>Date</Th>
          <Th>TSN</Th>
          <Th>CSN</Th>
          <Th>TSLSV</Th>
          <Th>CSLSV</Th>
          <Th>Facility</Th>
        </tr>
      </thead>
      <tbody className={extractionTbodyClass}>
        {rows.map((row, index) => {
          // `pageIds` is a jsonb-backed array — a row written mid-reprocessing can
          // reach the UI without it, so never map over it unguarded.
          const pageIds = row.pageIds ?? [];
          return (
            <tr
              key={`${row.shopVisitRef ?? 'visit'}-${pageIds[0] ?? 'page'}-${index}`}
              onClick={() => onInspect(row, index)}
              className={extractionRowClass}
            >
              {rowFindings && <FindingMarkerCell findings={rowFindings.get(index)} />}
              {pageIdToPosition && (
                <Td>
                  {pageIds.length > 0 ? (
                    <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                      {pageIds.map((pageId) => (
                        <SourcePageCell
                          key={pageId}
                          pageId={pageId}
                          pageIdToPosition={pageIdToPosition}
                        />
                      ))}
                    </div>
                  ) : (
                    '—'
                  )}
                </Td>
              )}
              <Td className="font-mono">{row.shopVisitRef ?? '—'}</Td>
              <Td className="font-mono">{formatWorkOrders(row.workOrders) ?? '—'}</Td>
              <Td>{formatWorkscopeLevel(row.workscopeLevel) ?? '—'}</Td>
              <TruncatedTd value={formatModulesWorked(row.modulesWorked)} />
              <Td>{row.date ?? '—'}</Td>
              <Td>{formatWorkscopeClock(row.tsn) ?? '—'}</Td>
              <Td>{formatWorkscopeClock(row.csn) ?? '—'}</Td>
              <Td>{formatWorkscopeClock(row.tslsv) ?? '—'}</Td>
              <Td>{formatWorkscopeClock(row.cslsv) ?? '—'}</Td>
              <TruncatedTd value={row.facility} />
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
